/**
 * Quality compound tool — check, re-evaluate.
 * Consolidates quality-check and re-evaluate into one tool.
 */

import type { GatewayRouteReturnByName } from "@productbrain/kernel-client";
import { McpServer } from "@modelcontextprotocol/sdk/server/mcp.js";
import { z } from "zod/v3";
import { thinWrapper, success, successResult, failureResult, parseOrFail, type ToolResult } from "../envelope.js";
import { kernelQuery, kernelMutation, getWorkspaceContext, requireWriteAccess, runWithToolContext } from "../client.js";
import { trackQualityCheck, trackQualityVerdict } from "../analytics.js";
import { formatRubricVerdictSection } from "./smart-capture.js";
import { dispatchDiscriminated, type ActionParamSpec } from "../compound-tool.js";
import { handleVerifyChain } from "./verify.js";
import { handleAuditRun } from "./audit.js";

// WP-484 S2 (§3): `quality` absorbs `verify` → verify-chain and `audit` →
// audit. Legacy `verify`/`audit` tools stay registered alongside (§7
// "alongside" mechanic) — both call the SAME handleVerifyChain/handleAuditRun
// functions, one business-logic path.
const QUALITY_ACTIONS = ["check", "re-evaluate", "verify-chain", "audit"] as const;
type QualityAction = (typeof QUALITY_ACTIONS)[number];

// §3a — the REGISTERED layer: flat ZodObject, action enum + superset of params.
export const qualitySchema = z.object({
  action: z.enum(QUALITY_ACTIONS).describe(
    "'check': read the entry's server quality verdict (tier + criteria). 're-evaluate': trigger fresh evaluation. " +
      "'verify-chain': verify entries against the codebase (codeMapping drift, cross-references) — absorbs `verify`. " +
      "'audit': hygiene audit for a bet entry — absorbs `audit`.",
  ),
  entryId: z.string().max(200).optional().describe("For 'check'/'re-evaluate'/'audit': entry ID, e.g. 'TEN-graph-db', '<PREFIX>-<n>'."),
  context: z.enum(["capture", "commit", "review"]).default("review").optional()
    .describe("For re-evaluate: evaluation context"),
  collection: z.string().max(200).optional().describe("For 'verify-chain': collection slug to verify (default: glossary)."),
  mode: z.enum(["report", "fix"]).optional().describe("For 'verify-chain': 'report' = read-only trust report (default). 'fix' = also update drifted codeMapping statuses."),
  phase: z.enum(["shaping", "handoff"]).optional().describe(
    "For 'audit': 'shaping' checks shaping-phase fields only (default). 'handoff' checks all required fields including buildContract/buildSequence/exclusions/risks.",
  ),
});

// §3b — the HANDLER layer: per-action discriminatedUnion.
const qualityCheckVariant = z.object({ action: z.literal("check"), entryId: z.string().max(200) });
const qualityReEvaluateVariant = z.object({
  action: z.literal("re-evaluate"),
  entryId: z.string().max(200),
  context: z.enum(["capture", "commit", "review"]).optional().default("review"),
});
const qualityVerifyChainVariant = z.object({
  action: z.literal("verify-chain"),
  collection: z.string().max(200).optional().default("glossary"),
  mode: z.enum(["report", "fix"]).optional().default("report"),
});
const qualityAuditVariant = z.object({
  action: z.literal("audit"),
  entryId: z.string().max(200),
  phase: z.enum(["shaping", "handoff"]).optional().default("shaping"),
});

export const qualityActionUnion = z.discriminatedUnion("action", [
  qualityCheckVariant,
  qualityReEvaluateVariant,
  qualityVerifyChainVariant,
  qualityAuditVariant,
]);

/** §4 backpressure text — one entry per action, never used for registration. */
export const QUALITY_ACTION_SPECS: Record<QualityAction, ActionParamSpec> = {
  check: { params: ["entryId"], description: "entryId is required." },
  "re-evaluate": { params: ["entryId", "context"], description: "entryId is required." },
  "verify-chain": { params: ["collection", "mode"], description: "All params optional; collection defaults to 'glossary'." },
  audit: { params: ["entryId", "phase"], description: "entryId is required." },
};

// WP-480 S1 (FEAT-1358): `check` reads the SERVER rubric verdict, not a client
// scorecard — `score`/`maxScore` drop in favour of the verdict tier + criteria.
export const qualityCheckOutputSchema = z.object({
  entryId: z.string(),
  /** WP-480 S1: false = the ID does not resolve to an entry (typo/deleted) — distinct from "no verdict yet". */
  entryFound: z.boolean().optional(),
  hasVerdict: z.boolean(),
  /** WP-480 S1: the verdict was judged against content the entry no longer has — re-evaluate for a current one. */
  stale: z.boolean().optional(),
  tier: z.string().optional(),
  passed: z.boolean().optional(),
  criteria: z.array(z.object({
    id: z.string(),
    passed: z.boolean(),
    hint: z.string().optional(),
  })),
});

export const qualityReevaluateOutputSchema = z.object({
  entryId: z.string(),
  context: z.string(),
  score: z.number(),
  maxScore: z.number(),
  improved: z.boolean(),
});

export function registerQualityTools(server: McpServer): void {
  // §3b handler map — one entry per action, dispatched via dispatchDiscriminated.
  const qualityHandlers: Record<QualityAction, (data: any) => Promise<ToolResult>> = {
    check: (data) => handleCheck(data.entryId),
    "re-evaluate": (data) => handleReEvaluate(data.entryId, data.context ?? "review"),
    "verify-chain": (data) => handleVerifyChain(server, { collection: data.collection ?? "glossary", mode: data.mode ?? "report" }),
    audit: (data) => handleAuditRun(data.entryId, data.phase ?? "shaping"),
  };

  server.registerTool(
    "quality",
    {
      title: "Quality",
      description:
        "Assess, re-evaluate, and verify entry/bet quality. Four actions:\n\n" +
        "- **check**: Read the entry's server quality verdict (tier + criteria) — it does not re-score. " +
        "Suggests links when the entry has no relations.\n" +
        "- **re-evaluate**: Trigger a fresh quality evaluation. Returns heuristic verdict immediately, " +
        "schedules LLM evaluation in background.\n" +
        "- **verify-chain**: Verify knowledge entries against the actual codebase (codeMapping drift, " +
        "cross-references) — absorbs `verify`. Produces a trust report with a trust score.\n" +
        "- **audit**: Run hygiene audit on a bet entry (13/18 gates) — absorbs `audit`.",
      inputSchema: qualitySchema,
      annotations: { readOnlyHint: false, destructiveHint: false, idempotentHint: true, openWorldHint: false },
    },
    thinWrapper(async (args) => {
      const parsed = parseOrFail(qualitySchema, args);
      if (!parsed.ok) return parsed.result;
      const { action } = parsed.data;

      return runWithToolContext({ tool: "quality", action }, () =>
        dispatchDiscriminated("quality", qualityActionUnion, parsed.data, QUALITY_ACTION_SPECS, qualityHandlers),
      );
    }),
  );
}

/**
 * Link-suggestion block — fires when the entry has zero relations. Runs for ANY
 * existing entry, INDEPENDENT of whether a verdict exists: `check`'s contract
 * promises "suggests links when the entry has no relations", and a missing/expired
 * verdict is exactly when an entry is most likely to be unlinked. (The deleted
 * client scorer ran this independently too; gating it behind a verdict silently
 * dropped the capability.)
 */
async function appendLinkSuggestions(entryId: string, lines: string[]): Promise<void> {
  let relationCount = -1;
  try {
    const relations = await kernelQuery<GatewayRouteReturnByName<"chain.listEntryRelations">>("chain.listEntryRelations", { entryId });
    relationCount = relations?.length ?? 0;
  } catch {
    // Relation count unavailable — skip the link suggestion; the verdict still renders.
    return;
  }
  if (relationCount !== 0) return;

  try {
    const suggestions = await kernelQuery<GatewayRouteReturnByName<"chain.graphSuggestLinks">>("chain.graphSuggestLinks", {
      entryId,
      maxHops: 2,
      limit: 3,
    });

    if ((suggestions?.suggestions?.length ?? 0) > 0) {
      const linkHints = (suggestions.suggestions ?? [])
        .map((s) => `  → \`relations action=create from="${entryId}" to="${s.entryId}" type="${s.recommendedRelationType}"\` — ${s.name} [${s.collectionSlug}] (${s.score}/100)`)
        .join("\n");

      lines.push(`\n## Suggested Links to Improve Quality\n${linkHints}`);
    }
  } catch {
    // Graph suggestions failed — quality check still works without them
  }
}

async function handleCheck(entryId: string) {
  // WP-480 S1 (FEAT-1358, DEC-32 thin wrapper): READ the server verdict and format
  // it. `check` never recomputes quality and holds no write access — only
  // `handleReEvaluate` writes a qualityVerdicts row. The server classifies
  // (entryFound / verdict / stale); this renders that classification verbatim.
  const res = await kernelQuery<GatewayRouteReturnByName<"quality.getLatestVerdictForEntry">>("quality.getLatestVerdictForEntry", { entryId });

  // The entry itself does not exist — a typo'd or deleted ID. Say so; do NOT send the
  // caller to `re-evaluate`, which cannot succeed and is a dead recovery path.
  if (!res || !res.entryFound) {
    return successResult(
      `# Quality Check: ${entryId}\n\nEntry \`${entryId}\` not found. Use \`search\` to find the right ID.`,
      `Entry ${entryId} not found — search for the correct ID.`,
      { entryId, entryFound: false, hasVerdict: false, criteria: [] as Array<{ id: string; passed: boolean; hint?: string }> },
      [{ tool: "search", description: "Find the entry", parameters: { query: entryId } }],
    );
  }

  const verdict = res.verdict;

  // Entry exists but has no verdict row (never evaluated, or the row expired) — stay
  // READ-ONLY and name the re-evaluate action. Do NOT silently trigger evaluation.
  // Link suggestions still run: they never depended on a verdict.
  if (!verdict) {
    const lines: string[] = [
      `# Quality Check: ${entryId}`,
      "",
      `No quality verdict yet for \`${entryId}\`. Run \`quality action=re-evaluate entryId="${entryId}"\` to evaluate it.`,
    ];
    await appendLinkSuggestions(entryId, lines);
    return successResult(
      lines.join("\n"),
      `No quality verdict yet for ${entryId} — re-evaluate to produce one.`,
      { entryId, entryFound: true, hasVerdict: false, criteria: [] as Array<{ id: string; passed: boolean; hint?: string }> },
      [{ tool: "quality", description: "Evaluate now", parameters: { action: "re-evaluate", entryId } }],
    );
  }

  // `verdict.criteria`'s nested type resolves to `any[]` (a documented nested-any residual
  // class, same shape as the other `chain.*` entry-data cases — routeContract.test.ts's own
  // PROVISIONAL note on nested holes not caught by its top-level-only detector). Annotated
  // here to the fields this file actually reads (PR #395, Lane C S3b annotation pass).
  const criteria: Array<{ id: string; passed: boolean; hint?: string }> = verdict.criteria ?? [];
  const lines: string[] = [`# Quality Check: ${entryId}`, ""];
  // A verdict judged against content the entry no longer has is stale — label it as
  // such rather than presenting a pre-edit pass/fail as the entry's current quality.
  if (res.stale) {
    lines.push(
      `> STALE — this verdict was judged against an earlier version of \`${entryId}\`, which has been edited since. ` +
      `Run \`quality action=re-evaluate entryId="${entryId}"\` for a current verdict.`,
      "",
    );
  }
  const verdictSection = formatRubricVerdictSection(verdict);
  lines.push(verdictSection || `Verdict tier \`${verdict.tier ?? "unknown"}\` for \`${entryId}\` — no rubric criteria to report.`);

  await appendLinkSuggestions(entryId, lines);

  try {
    const wsForTracking = await getWorkspaceContext();
    trackQualityCheck(wsForTracking.workspaceId, {
      entry_id: entryId,
      entry_type: verdict.canonicalKey ?? "",
      tier: verdict.tier ?? "",
      passed: verdict.passed ?? false,
      source: verdict.source ?? "",
      llm_status: verdict.llmStatus,
      llm_duration_ms: verdict.llmDurationMs,
      llm_error: verdict.llmError,
      has_roger_martin: !!verdict.rogerMartin,
    });
  } catch { /* tracking is advisory */ }

  const passedCount = criteria.filter((c) => c.passed).length;
  return successResult(
    lines.join("\n"),
    `Quality check for ${entryId}: ${verdict.tier ?? "unknown"} tier, ${passedCount}/${criteria.length} criteria pass.` +
      (res.stale ? " STALE — the entry was edited after this verdict; re-evaluate for a current one." : ""),
    {
      entryId,
      entryFound: true,
      hasVerdict: true,
      stale: res.stale ?? false,
      tier: verdict.tier,
      passed: verdict.passed,
      criteria: criteria.map((c) => ({ id: c.id, passed: c.passed, hint: c.hint })),
    },
    [
      { tool: "relations", description: "Suggest connections", parameters: { action: "suggest", entryId } },
    ],
  );
}

async function handleReEvaluate(entryId: string, context: "capture" | "commit" | "review") {
  requireWriteAccess();
  const ws = await getWorkspaceContext();
  const result = await kernelMutation<GatewayRouteReturnByName<"quality.reEvaluateEntry">>("quality.reEvaluateEntry", {
    entryId,
    context,
  });

  if (!result) {
    return failureResult(
      `Entry '${entryId}' not found or has no rubric.`,
      "NOT_FOUND",
      `Entry '${entryId}' not found or has no rubric.`,
      "Use entries action=search to find the correct ID.",
      [{ tool: "entries", description: "Search entries", parameters: { action: "search", query: entryId } }],
    );
  }

  const llmScheduled = result.verdict.tier !== "passive";
  const lines: string[] = [`Re-evaluated \`${entryId}\` in \`${context}\` context.`];
  lines.push("");
  lines.push(formatRubricVerdictSection({
    ...result.verdict,
    source: result.source,
    llmStatus: llmScheduled ? "pending" : "skipped",
  }));

  try {
    trackQualityVerdict(ws.workspaceId, {
      entry_id: entryId,
      entry_type: result.verdict.canonicalKey ?? "",
      tier: result.verdict.tier ?? "",
      context,
      passed: result.verdict.passed ?? false,
      source: "heuristic",
      criteria_total: result.verdict.criteria?.length ?? 0,
      criteria_failed: result.verdict.criteria?.filter((c) => !c.passed).length ?? 0,
      llm_scheduled: llmScheduled,
      methodology_version: result.verdict.methodologyVersion,
    });
  } catch { /* tracking is advisory */ }

  const criteria = result.verdict.criteria ?? [];
  const reEvalScore = criteria.filter((c) => c.passed).length;
  const reEvalMaxScore = criteria.length;

  return {
    content: [{ type: "text" as const, text: lines.join("\n") }],
    structuredContent: success(
      `Re-evaluated ${entryId} in ${context} context. Score: ${reEvalScore}/${reEvalMaxScore}.`,
      {
        entryId,
        context,
        score: reEvalScore,
        maxScore: reEvalMaxScore,
        improved: result.verdict.passed ?? false,
      },
    ),
  };
}
